import React, { useEffect, useMemo, useState, useCallback } from "react";
import dataApi, { MetricsTimeRangeResponse } from "../../../Api/data.api";
import { PlotlyChart } from "../../Data/Dataset/components/PlotlyChart";
import { SelectedMetric, DEFAULT_CHART_SETTINGS, METRIC_COLORS } from "../../Data/Dataset/types";

interface BatchMetricsChartProps {
  batchId: number;
}

const styles: Record<string, React.CSSProperties> = {
  wrap: { display: "flex", flexDirection: "column", gap: "1rem" },
  meta: { fontSize: "0.85rem", color: "#8b949e" },
  chips: { display: "flex", flexWrap: "wrap", gap: "0.4rem" },
  chip: {
    display: "flex",
    alignItems: "center",
    gap: "0.4rem",
    padding: "0.3rem 0.6rem",
    borderRadius: "999px",
    border: "1px solid rgba(108, 117, 125, 0.4)",
    background: "rgba(33, 40, 44, 0.6)",
    color: "#c5c5c5",
    fontSize: "0.78rem",
    cursor: "pointer",
  },
  swatch: { width: "10px", height: "10px", borderRadius: "3px", flexShrink: 0 },
};

export default function BatchMetricsChart({ batchId }: BatchMetricsChartProps) {
  const [response, setResponse] = useState<MetricsTimeRangeResponse | null>(null);
  const [selectedMetrics, setSelectedMetrics] = useState<SelectedMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await dataApi.getBatchMetrics(batchId);
        if (cancelled) return;
        setResponse(res);
        const metrics: SelectedMetric[] = (res.metricKeys || []).map((key, i) => ({
          key,
          label: key,
          color: METRIC_COLORS[i % METRIC_COLORS.length],
          visible: true,
        }));
        setSelectedMetrics(metrics);
      } catch (err: any) {
        if (!cancelled) setError(err.message || "Greška pri učitavanju metrika");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [batchId]);

  const toggleMetric = useCallback((key: string) => {
    setSelectedMetrics((prev) =>
      prev.map((m) => (m.key === key ? { ...m, visible: !m.visible } : m))
    );
  }, []);

  const dataExtent = useMemo(() => {
    if (!response || response.data.length === 0) return null;
    const from = response.timeRange.actualFrom ? new Date(response.timeRange.actualFrom) : null;
    const to = response.timeRange.actualTo ? new Date(response.timeRange.actualTo) : null;
    return from && to ? { from, to } : null;
  }, [response]);

  if (loading) {
    return <p className="empty-state">Učitavanje metrika…</p>;
  }

  if (error) {
    return <p className="empty-state">{error}</p>;
  }

  if (!response || response.data.length === 0) {
    return (
      <p className="empty-state">
        Za ovaj batch nema povezanih metrika. Poveži ih u prikazu dataseta (Assign batch).
      </p>
    );
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.meta}>
        {response.totalCount} mjerenja ·{" "}
        {response.timeRange.actualFrom
          ? new Date(response.timeRange.actualFrom).toLocaleString()
          : "—"}{" "}
        →{" "}
        {response.timeRange.actualTo
          ? new Date(response.timeRange.actualTo).toLocaleString()
          : "—"}
      </div>

      <div style={styles.chips}>
        {selectedMetrics.map((m) => (
          <button
            key={m.key}
            style={{ ...styles.chip, opacity: m.visible ? 1 : 0.45 }}
            onClick={() => toggleMetric(m.key)}
            type="button"
          >
            <span style={{ ...styles.swatch, background: m.color }} />
            {m.label}
          </button>
        ))}
      </div>

      <PlotlyChart
        data={response.data as any}
        dataExtent={dataExtent}
        selectedMetrics={selectedMetrics}
        chartSettings={DEFAULT_CHART_SETTINGS}
        height={520}
      />
    </div>
  );
}
